import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/Skills.css';
import Particles from './Particles';

const Skills = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [clickedIndex, setClickedIndex] = useState(null);
  const skillRefs = useRef([]);

  const handleSkillClick = useCallback((clickedIdx) => {
    setClickedIndex(clickedIdx);
    
    // Create ripple wave effect from clicked skill
    skillRefs.current.forEach((ref, idx) => {
      if (ref) {
        const distance = Math.abs(idx - clickedIdx);
        const delay = distance * 50;
        
        setTimeout(() => {
          ref.classList.add('skill-wave');
          setTimeout(() => {
            ref.classList.remove('skill-wave');
          }, 600);
        }, delay);
      }
    });

    setTimeout(() => setClickedIndex(null), 2000);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.2 }
    );

    const element = document.getElementById('skills');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  // Automatic wave animation every 2.5 seconds
  useEffect(() => {
    if (!isVisible) return;

    const autoAnimate = setInterval(() => {
      const totalSkills = skillRefs.current.length;
      if (totalSkills > 0) {
        const randomIndex = Math.floor(Math.random() * totalSkills);
        handleSkillClick(randomIndex);
      }
    }, 2500);

    return () => clearInterval(autoAnimate);
  }, [isVisible, handleSkillClick]);

  const skillsData = [
    { category: 'Languages', skills: ['Python', 'JavaScript', 'TypeScript', 'Java'] },
    { category: 'Web Development', skills: ['React.js', 'Node.js', 'Express.js', 'FastAPI', 'Tailwind CSS', 'HTML5 & CSS3'] },
    { category: 'AI & Automation', skills: ['LangChain', 'TensorFlow', 'Keras', 'Scikit-learn', 'NLP'] },
    { category: 'Databases', skills: ['PostgreSQL', 'MySQL', 'ChromaDB', 'Firebase'] },
    { category: 'Libraries & Tools', skills: ['Git', 'Docker', 'Vercel', 'Postman', 'REST APIs', 'GSAP', 'Framer Motion', 'Three.js'] }
  ];

  // Flatten all skills for unified indexing
  const allSkills = [];
  let globalIndex = 0;
  skillsData.forEach(category => {
    category.skills.forEach(skill => {
      allSkills.push({ skill, category: category.category, globalIndex: globalIndex++ });
    });
  });

  return (
    <div className={`skills-section ${isVisible ? 'visible' : ''}`} style={{ background: 'transparent', position: 'relative' }}>
      <div className="skills-background" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
        <Particles
          particleColors={["#ffffff"]}
          particleCount={60}
          particleSpread={10}
          speed={0.05}
          particleBaseSize={60}
          moveParticlesOnHover={false}
          alphaParticles={true}
          disableRotation={false}
          pixelRatio={1}
        />
      </div>
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <h2 className="section-title">
          <span className="title-number">05.</span>
          Skills & Technologies
        </h2>

        <div className="skills-content">
          <div className="skills-grid">
            {skillsData.map((categoryData, catIndex) => (
              <div key={catIndex} className="skills-category">
                <h3 className="category-title">{categoryData.category}</h3>
                <div className="skills-tags">
                  {categoryData.skills.map((skill, skillIndex) => {
                    const globalIdx = allSkills.findIndex(s => s.skill === skill && s.category === categoryData.category);
                    return (
                      <span
                        key={skillIndex}
                        ref={el => skillRefs.current[globalIdx] = el}
                        className={`skill-tag ${clickedIndex === globalIdx ? 'clicked' : ''}`}
                        onClick={() => handleSkillClick(globalIdx)}
                      >
                        {skill}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Skills;
